import { InstallLocation } from "@yardzen/api-dtos";

// Types for wishlistMappings
export type WishlistOption = {
  displayText: string;
  projectElementName: string;
  priceRangeOptions: {
    text: string;
    priceLow: number;
    priceHigh: number;
    iconName: string;
  }[];
  required: boolean;
  slug: string;
  selectedPriceLow: number | null;
  selectedPriceHigh: number | null;
  selectedInstallLocation: InstallLocation[] | null;
  notes: string;
  priority: number | null;
};

export type WishlistCategory = {
  headerText: string;
  options: WishlistOption[];
};

export type WishlistMappings = {
  planting: WishlistCategory;
  entertainment: WishlistCategory;
  hardscape: WishlistCategory;
  playAreas: WishlistCategory;
  structural: WishlistCategory;
  waterFeatures: WishlistCategory;
};

// Helper to format price range for display
export function formatPriceRange(low: number, high: number) {
  // Convert pennies to dollars for display
  return `$${(low / 100).toLocaleString()} - $${(high / 100).toLocaleString()}`;
}

// Utility to generate slugs from displayText
function toSlug(str: string) {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

export const wishlistMappings = {
  planting: {
    headerText: "Are there any special planting features you'd like to have?",
    options: [
      {
        displayText: "General Planting",
        projectElementName: "Planting",
        priceRangeOptions: [
          {
            text: "Sparse Planting",
            priceLow: 500000,
            priceHigh: 1500000,
            iconName: "planting-sparse",
          },
          {
            text: "Moderate Planting",
            priceLow: 750000,
            priceHigh: 2200000,
            iconName: "planting-moderate",
          },
          {
            text: "Dense Planting",
            priceLow: 1000000,
            priceHigh: 3000000,
            iconName: "planting-dense",
          },
        ],
        required: false,
        slug: toSlug("General Planting"),
        selectedPriceLow: null,
        selectedPriceHigh: null,
        selectedInstallLocation: null,
        notes: "",
        priority: null,
      },
      {
        displayText: "Lawn/Turf",
        projectElementName: "Lawn Installation",
        priceRangeOptions: [
          {
            text: "Small lawn (around 500 sqft).",
            priceLow: 350000,
            priceHigh: 900000,
            iconName: "lawn-small",
          },
          {
            text: "Medium sized lawn (around 1,000 sqft).",
            priceLow: 700000,
            priceHigh: 1800000,
            iconName: "lawn-medium",
          },
          {
            text: "Large lawn (3,000 sqft).",
            priceLow: 2100000,
            priceHigh: 5400000,
            iconName: "lawn-large",
          },
        ],
        required: false,
        slug: toSlug("Lawn/Turf"),
        selectedPriceLow: null,
        selectedPriceHigh: null,
        selectedInstallLocation: null,
        notes: "",
        priority: null,
      },
      {
        displayText: "Raised Beds",
        projectElementName: "Raised Beds",
        priceRangeOptions: [
          {
            text: "Small sized garden (2-4 raised beds).",
            priceLow: 50000,
            priceHigh: 200000,
            iconName: "raised-beds-small",
          },
          {
            text: "Medium sized garden (5-7 raised beds).",
            priceLow: 200000,
            priceHigh: 500000,
            iconName: "raised-beds-medium",
          },
          {
            text: "Extensive garden (7+ raised beds with fencing).",
            priceLow: 500000,
            priceHigh: 700000,
            iconName: "raised-beds-large",
          },
        ],
        required: false,
        slug: toSlug("Raised Beds"),
        selectedPriceLow: null,
        selectedPriceHigh: null,
        selectedInstallLocation: null,
        notes: "",
        priority: null,
      },
      {
        displayText: "Trees",
        projectElementName: "Trees",
        priceRangeOptions: [
          {
            text: "1-2 immature trees.",
            priceLow: 60000,
            priceHigh: 80000,
            iconName: "trees-small",
          },
          {
            text: "2-5 immature trees.",
            priceLow: 120000,
            priceHigh: 300000,
            iconName: "trees-medium",
          },
          {
            text: "Large mature tree.",
            priceLow: 700000,
            priceHigh: 1000000,
            iconName: "trees-large",
          },
        ],
        required: false,
        slug: toSlug("Trees"),
        selectedPriceLow: null,
        selectedPriceHigh: null,
        selectedInstallLocation: null,
        notes: "",
        priority: null,
      },
    ],
  },
  entertainment: {
    headerText: "Which entertainment elements catch your attention?",
    options: [
      {
        displayText: "Firepit",
        projectElementName: "Fire Pit",
        priceRangeOptions: [
          {
            text: "Pre-fabricated wood or propane fire pit",
            priceLow: 20000,
            priceHigh: 150000,
            iconName: "firepit-small",
          },
          {
            text: "Higher quality, standalone, propane fire pit",
            priceLow: 150000,
            priceHigh: 400000,
            iconName: "firepit-medium",
          },
          {
            text: "Custom built firepit or fireplace with gas lines",
            priceLow: 500000,
            priceHigh: 2200000,
            iconName: "firepit-large",
          },
        ],
        required: false,
        slug: toSlug("Firepit"),
        selectedPriceLow: null,
        selectedPriceHigh: null,
        selectedInstallLocation: null,
        notes: "",
        priority: null,
      },
      {
        displayText: "Outdoor Kitchen",
        projectElementName: "Outdoor Kitchen",
        priceRangeOptions: [
          {
            text: "Pre-fabricated outdoor kitchen.",
            priceLow: 1500000,
            priceHigh: 1800000,
            iconName: "outdoor-kitchen-small-medium",
          },
          {
            text: "Built-in gas grill with 3 feet of counter space.",
            priceLow: 1800000,
            priceHigh: 2200000,
            iconName: "outdoor-kitchen-small-medium",
          },
          {
            text: "Built-in gas grill with 3 feet of counter space. Plus an add-on such as a refrigerator, gas cooktop, or sink.",
            priceLow: 2200000,
            priceHigh: 2500000,
            iconName: "outdoor-kitchen-large",
          },
        ],
        required: false,
        slug: toSlug("Outdoor Kitchen"),
        selectedPriceLow: null,
        selectedPriceHigh: null,
        selectedInstallLocation: null,
        notes: "",
        priority: null,
      },
      {
        displayText: "Pergola",
        projectElementName: "Pergola",
        priceRangeOptions: [
          {
            text: "Pre-fab pergola. Ready to assemble pergola kits.",
            priceLow: 500000,
            priceHigh: 1000000,
            iconName: "pergola-small",
          },
          {
            text: "Small to medium custom made pergola. May require permitting.",
            priceLow: 1000000,
            priceHigh: 2000000,
            iconName: "pergola-medium-large",
          },
          {
            text: "Custom large pergola. Requires permitting.",
            priceLow: 2200000,
            priceHigh: 3500000,
            iconName: "pergola-medium-large",
          },
        ],
        required: false,
        slug: toSlug("Pergola"),
        selectedPriceLow: null,
        selectedPriceHigh: null,
        selectedInstallLocation: null,
        notes: "",
        priority: null,
      },
      {
        displayText: "Grill",
        projectElementName: "BBQ Area",
        priceRangeOptions: [
          {
            text: "Charcoal or gas grill",
            priceLow: 20000,
            priceHigh: 150000,
            iconName: "grill-small",
          },
          {
            text: "Kamado or gas grill with prep space",
            priceLow: 50000,
            priceHigh: 300000,
            iconName: "grill-medium",
          },
          {
            text: "High end charcoal or gas grill",
            priceLow: 150000,
            priceHigh: 600000,
            iconName: "grill-large",
          },
        ],
        required: false,
        slug: toSlug("Grill"),
        selectedPriceLow: null,
        selectedPriceHigh: null,
        selectedInstallLocation: null,
        notes: "",
        priority: null,
      },
    ],
  },
  hardscape: {
    headerText: "Do you need any new hardscape to expand your living areas?",
    options: [
      {
        displayText: "Pathway",
        projectElementName: "Pathways",
        priceRangeOptions: [
          {
            text: "Small pathway (250 sqft). Decomposed granite or gravel, simple aesthetic.",
            priceLow: 200000,
            priceHigh: 375000,
            iconName: "pathway-small",
          },
          {
            text: "Medium sized pathways (500 sqft).",
            priceLow: 375000,
            priceHigh: 750000,
            iconName: "pathway-medium",
          },
          {
            text: "Large/long pathways (750 sqft).",
            priceLow: 750000,
            priceHigh: 1500000,
            iconName: "pathway-large",
          },
        ],
        required: false,
        slug: toSlug("Pathway"),
        selectedPriceLow: null,
        selectedPriceHigh: null,
        selectedInstallLocation: null,
        notes: "",
        priority: null,
      },
      {
        displayText: "Gravel Patio",
        projectElementName: "Gravel Patio",
        priceRangeOptions: [
          {
            text: "Small gravel or decomposed granite patio. (Approximately 100 SF)",
            priceLow: 70000,
            priceHigh: 120000,
            iconName: "gravel-patio-small",
          },
          {
            text: "Medium sized gravel or decomposed granite patio. (Approximately 500 SF)",
            priceLow: 350000,
            priceHigh: 600000,
            iconName: "gravel-patio-medium-large",
          },
          {
            text: "Large sized gravel or decomposed granite patio. (Approximately 750 SF or larger)",
            priceLow: 520000,
            priceHigh: 900000,
            iconName: "gravel-patio-medium-large",
          },
        ],
        required: false,
        slug: toSlug("Gravel Patio"),
        selectedPriceLow: null,
        selectedPriceHigh: null,
        selectedInstallLocation: null,
        notes: "",
        priority: null,
      },
      {
        displayText: "Paved Patio",
        projectElementName: "Paved Patio",
        priceRangeOptions: [
          {
            text: "Small patio (100-200 square ft).",
            priceLow: 350000,
            priceHigh: 700000,
            iconName: "paved-patio-small",
          },
          {
            text: "Medium patio (500 square ft).",
            priceLow: 1750000,
            priceHigh: 3000000,
            iconName: "paved-patio-medium-large",
          },
          {
            text: "Large patio (750 square ft).",
            priceLow: 3000000,
            priceHigh: 4500000,
            iconName: "paved-patio-medium-large",
          },
        ],
        required: false,
        slug: toSlug("Paved Patio"),
        selectedPriceLow: null,
        selectedPriceHigh: null,
        selectedInstallLocation: null,
        notes: "",
        priority: null,
      },
      {
        displayText: "Driveway",
        projectElementName: "Driveway",
        priceRangeOptions: [
          {
            text: "Small Driveway (1 car).",
            priceLow: 1000000,
            priceHigh: 1500000,
            iconName: "driveway-small",
          },
          {
            text: "Medium Sized Driveway (2 car).",
            priceLow: 2000000,
            priceHigh: 3000000,
            iconName: "driveway-medium",
          },
          {
            text: "Large Driveway (4 cars+ or particularly long driveway).",
            priceLow: 3000000,
            priceHigh: 5000000,
            iconName: "driveway-large",
          },
        ],
        required: false,
        slug: toSlug("Driveway"),
        selectedPriceLow: null,
        selectedPriceHigh: null,
        selectedInstallLocation: null,
        notes: "",
        priority: null,
      },
      {
        displayText: "Deck",
        projectElementName: "Deck",
        priceRangeOptions: [
          {
            text: "Small deck (200 sqft).",
            priceLow: 1100000,
            priceHigh: 2200000,
            iconName: "deck-small",
          },
          {
            text: "Medium sized deck (300 sqft).",
            priceLow: 2200000,
            priceHigh: 3300000,
            iconName: "deck-medium",
          },
          {
            text: "Large deck (Approximately 600+ sqft), Premium hardwoods and custom railings.",
            priceLow: 3300000,
            priceHigh: 9000000,
            iconName: "deck-large",
          },
        ],
        required: false,
        slug: toSlug("Deck"),
        selectedPriceLow: null,
        selectedPriceHigh: null,
        selectedInstallLocation: null,
        notes: "",
        priority: null,
      },
    ],
  },
  playAreas: {
    headerText: "Choose some play areas for kids and adults alike.",
    options: [
      {
        displayText: "Play Structure",
        projectElementName: "Outdoor Play Area",
        priceRangeOptions: [
          {
            text: "Simple playhouse or  playground with swing and slide.",
            priceLow: 100000,
            priceHigh: 300000,
            iconName: "play-structure-small",
          },
          {
            text: "Small to medium custom play area integrated into natural landscape with items like climbing walls and slides.",
            priceLow: 400000,
            priceHigh: 800000,
            iconName: "play-structure-medium",
          },
          {
            text: "Large custom play area integrated into natural landscape with items like climbing walls and slides.",
            priceLow: 1000000,
            priceHigh: 1500000,
            iconName: "play-structure-large",
          },
        ],
        required: false,
        slug: toSlug("Play Structure"),
        selectedPriceLow: null,
        selectedPriceHigh: null,
        selectedInstallLocation: null,
        notes: "",
        priority: null,
      },
    ],
  },
  structural: {
    headerText:
      "Are there any new structural add-ons that you need? If you're not sure, no problem",
    options: [
      {
        displayText: "Short Fencing",
        projectElementName: "Short Fencing",
        priceRangeOptions: [
          {
            text: "Short length fence (50-100ft).",
            priceLow: 400000,
            priceHigh: 800000,
            iconName: "short-fencing-small",
          },
          {
            text: "Medium length fence (150ft-250ft).",
            priceLow: 1200000,
            priceHigh: 2000000,
            iconName: "short-fencing-medium",
          },
          {
            text: "Long fence (250ft +).",
            priceLow: 2000000,
            priceHigh: 3000000,
            iconName: "short-fencing-large",
          },
        ],
        required: false,
        slug: toSlug("Short Fencing"),
        selectedPriceLow: null,
        selectedPriceHigh: null,
        selectedInstallLocation: null,
        notes: "",
        priority: null,
      },
      {
        displayText: "Tall Fencing",
        projectElementName: "Tall Fencing",
        priceRangeOptions: [
          {
            text: "Short length fence (50-100ft).",
            priceLow: 600000,
            priceHigh: 1200000,
            iconName: "tall-fencing-small",
          },
          {
            text: "Medium length fence (150ft-250ft).",
            priceLow: 1800000,
            priceHigh: 3000000,
            iconName: "tall-fencing-medium",
          },
          {
            text: "Long fence (250ft +).",
            priceLow: 3000000,
            priceHigh: 4000000,
            iconName: "tall-fencing-large",
          },
        ],
        required: false,
        slug: toSlug("Tall Fencing"),
        selectedPriceLow: null,
        selectedPriceHigh: null,
        selectedInstallLocation: null,
        notes: "",
        priority: null,
      },
      {
        displayText: "Retaining Walls",
        projectElementName: "Retaining Wall",
        priceRangeOptions: [
          {
            text: "Small retaining wall (3` x 20`).",
            priceLow: 300000,
            priceHigh: 900000,
            iconName: "retaining-wall-small",
          },
          {
            text: "Medium sized retaining wall (3` x 40`).",
            priceLow: 900000,
            priceHigh: 1800000,
            iconName: "retaining-wall-medium",
          },
          {
            text: "Large retaining wall (3` x 80`).",
            priceLow: 1800000,
            priceHigh: 3600000,
            iconName: "retaining-wall-large",
          },
        ],
        required: false,
        slug: toSlug("Retaining Walls"),
        selectedPriceLow: null,
        selectedPriceHigh: null,
        selectedInstallLocation: null,
        notes: "",
        priority: null,
      },
      {
        displayText: "Outdoor Lighting",
        projectElementName: "Outdoor Lighting",
        priceRangeOptions: [
          {
            text: "Simple outdoor lighting elements, like pathway lighting. 1-5 low voltage light fixtures",
            priceLow: 50000,
            priceHigh: 100000,
            iconName: "outdoor-lighting-small",
          },
          {
            text: "A few outdoor lighting elements, like pathway lighting, deck lighting, or wall lights. 6-12 low voltage light fixtures",
            priceLow: 200000,
            priceHigh: 300000,
            iconName: "outdoor-lighting-medium",
          },
          {
            text: "Extensive outdoor lighting for most yard features. 12 + light fixtures",
            priceLow: 500000,
            priceHigh: 1500000,
            iconName: "outdoor-lighting-large",
          },
        ],
        required: false,
        slug: toSlug("Outdoor Lighting"),
        selectedPriceLow: null,
        selectedPriceHigh: null,
        selectedInstallLocation: null,
        notes: "",
        priority: null,
      },
      {
        displayText: "Driveway Gate",
        projectElementName: "Driveway Gate",
        priceRangeOptions: [
          {
            text: "Swing gate.",
            priceLow: 500000,
            priceHigh: 1000000,
            iconName: "driveway-gate-swing",
          },
          {
            text: "Sliding gate.",
            priceLow: 1000000,
            priceHigh: 1500000,
            iconName: "driveway-gate-sliding",
          },
          {
            text: "Automatic gate.",
            priceLow: 1500000,
            priceHigh: 2000000,
            iconName: "driveway-gate-automatic",
          },
        ],
        required: false,
        slug: toSlug("Driveway Gate"),
        selectedPriceLow: null,
        selectedPriceHigh: null,
        selectedInstallLocation: null,
        notes: "",
        priority: null,
      },
      {
        displayText: "Railings",
        projectElementName: "Railings",
        priceRangeOptions: [
          {
            text: "One set of railings. For stairs, deck, or retaining walls. ",
            priceLow: 200000,
            priceHigh: 550000,
            iconName: "railings-small",
          },
          {
            text: "Multiple sets of railings. For stairs, deck, or retaining walls.",
            priceLow: 400000,
            priceHigh: 1100000,
            iconName: "railings-medium",
          },
          {
            text: "3+ sets of railings. For stairs, deck, or retaining walls.",
            priceLow: 600000,
            priceHigh: 1650000,
            iconName: "railings-large",
          },
        ],
        required: false,
        slug: toSlug("Railings"),
        selectedPriceLow: null,
        selectedPriceHigh: null,
        selectedInstallLocation: null,
        notes: "",
        priority: null,
      },
      {
        displayText: "Stairs",
        projectElementName: "Stairs",
        priceRangeOptions: [
          {
            text: "One small exterior stairway (3-5 steps), no railing included. ",
            priceLow: 100000,
            priceHigh: 300000,
            iconName: "stairs-small",
          },
          {
            text: "2-3 small exterior steps, no railing included.",
            priceLow: 300000,
            priceHigh: 600000,
            iconName: "stairs-medium",
          },
          {
            text: "3+ sets of small exterior steps, no railing included.",
            priceLow: 600000,
            priceHigh: 900000,
            iconName: "stairs-large",
          },
        ],
        required: false,
        slug: toSlug("Stairs"),
        selectedPriceLow: null,
        selectedPriceHigh: null,
        selectedInstallLocation: null,
        notes: "",
        priority: null,
      },
      {
        displayText: "Staircase (Rises One Level)",
        projectElementName: "Staircase",
        priceRangeOptions: [
          {
            text: "One custom stairway that rises one level.",
            priceLow: 750000,
            priceHigh: 1500000,
            iconName: "staircase-small",
          },
          {
            text: "Multiple custom stairways that rise one level.",
            priceLow: 1500000,
            priceHigh: 3000000,
            iconName: "staircase-medium",
          },
          {
            text: "3 custom stairways that rise one level.",
            priceLow: 3000000,
            priceHigh: 6000000,
            iconName: "staircase-large",
          },
        ],
        required: false,
        slug: toSlug("Staircase"),
        selectedPriceLow: null,
        selectedPriceHigh: null,
        selectedInstallLocation: null,
        notes: "",
        priority: null,
      },
    ],
  },
  waterFeatures: {
    headerText:
      "How do you feel about adding a water feature to your outdoor space?",
    options: [
      {
        displayText: "Pool",
        projectElementName: "Pool",
        priceRangeOptions: [
          {
            text: "Above ground modular pool or plunge pool.",
            priceLow: 6000000,
            priceHigh: 7500000,
            iconName: "pool-small",
          },
          {
            text: "Small to medium in-ground pool. Requires the work of an engineer.",
            priceLow: 8000000,
            priceHigh: 10000000,
            iconName: "pool-medium",
          },
          {
            text: "Custom in-ground pool. Baja shelf or spa included. Requires the work of an engineer.",
            priceLow: 10000000,
            priceHigh: 15000000,
            iconName: "pool-large",
          },
        ],
        required: false,
        slug: toSlug("Pool"),
        selectedPriceLow: null,
        selectedPriceHigh: null,
        selectedInstallLocation: null,
        notes: "",
        priority: null,
      },
      {
        displayText: "Hot Tub",
        projectElementName: "Hot Tub",
        priceRangeOptions: [
          {
            text: "Above-ground hot tub. Simple tub, short electrical run, and simple foundation for tub.",
            priceLow: 600000,
            priceHigh: 1000000,
            iconName: "hot-tub-small",
          },
          {
            text: "Mid-range above-ground hot tub with higher end materials.",
            priceLow: 800000,
            priceHigh: 1200000,
            iconName: "hot-tub-medium",
          },
          {
            text: "Luxury or in-ground hot tub. Requires the work of an engineer.",
            priceLow: 2500000,
            priceHigh: 3000000,
            iconName: "hot-tub-large",
          },
        ],
        required: false,
        slug: toSlug("Hot Tub"),
        selectedPriceLow: null,
        selectedPriceHigh: null,
        selectedInstallLocation: null,
        notes: "",
        priority: null,
      },
      {
        displayText: "Outdoor Shower",
        projectElementName: "Outdoor Shower",
        priceRangeOptions: [
          {
            text: "Simple, open pre-fab shower with hot and cold running water. Requires permitting and plumber/sewer connection.",
            priceLow: 800000,
            priceHigh: 1000000,
            iconName: "outdoor-shower-small",
          },
          {
            text: "A moderate sized, partially enclosed, shower with a more extensive enclosure. Requires permitting and plumber/sewer connection.",
            priceLow: 1000000,
            priceHigh: 1200000,
            iconName: "outdoor-shower-medium",
          },
          {
            text: "Elaborate outdoor shower with large enclosure. Requires permitting and plumber/sewer connection.",
            priceLow: 1200000,
            priceHigh: 1500000,
            iconName: "outdoor-shower-large",
          },
        ],
        required: false,
        slug: toSlug("Outdoor Shower"),
        selectedPriceLow: null,
        selectedPriceHigh: null,
        selectedInstallLocation: null,
        notes: "",
        priority: null,
      },
      {
        displayText: "Fountain",
        projectElementName: "Fountain",
        priceRangeOptions: [
          {
            text: "Small pre-fabricated fountain, may or may not connected to plumbing system.",
            priceLow: 200000,
            priceHigh: 300000,
            iconName: "fountain-small",
          },
          {
            text: "Medium sized fountain, pre-fabricated, may or may not be connected to plumbing system.",
            priceLow: 500000,
            priceHigh: 600000,
            iconName: "fountain-medium",
          },
          {
            text: "Custom fountain, medium to large size, connected to plumbing system.",
            priceLow: 600000,
            priceHigh: 1000000,
            iconName: "fountain-large",
          },
        ],
        required: false,
        slug: toSlug("Fountain"),
        selectedPriceLow: null,
        selectedPriceHigh: null,
        selectedInstallLocation: null,
        notes: "",
        priority: null,
      },
    ],
  },
};

// Yard option label mappings by InstallLocation
export const YARD_OPTION_LABELS: Record<InstallLocation, string> = {
  [InstallLocation.FRONTYARD]: "Front Yard",
  [InstallLocation.BACKYARD]: "Backyard",
  [InstallLocation.LEFTYARD]: "Side Yard Left",
  [InstallLocation.RIGHTYARD]: "Side Yard Right",
};

// Yard options by package type
export const PACKAGE_TYPE_YARD_OPTIONS: Record<
  string,
  { label: string; value: InstallLocation }[]
> = {
  FRONT_YARD: [
    {
      label: YARD_OPTION_LABELS[InstallLocation.FRONTYARD],
      value: InstallLocation.FRONTYARD,
    },
    {
      label: YARD_OPTION_LABELS[InstallLocation.LEFTYARD],
      value: InstallLocation.LEFTYARD,
    },
    {
      label: YARD_OPTION_LABELS[InstallLocation.RIGHTYARD],
      value: InstallLocation.RIGHTYARD,
    },
  ],
  BACK_YARD: [
    {
      label: YARD_OPTION_LABELS[InstallLocation.BACKYARD],
      value: InstallLocation.BACKYARD,
    },
    {
      label: YARD_OPTION_LABELS[InstallLocation.LEFTYARD],
      value: InstallLocation.LEFTYARD,
    },
    {
      label: YARD_OPTION_LABELS[InstallLocation.RIGHTYARD],
      value: InstallLocation.RIGHTYARD,
    },
  ],
  FULL_YARD: [
    {
      label: YARD_OPTION_LABELS[InstallLocation.FRONTYARD],
      value: InstallLocation.FRONTYARD,
    },
    {
      label: YARD_OPTION_LABELS[InstallLocation.BACKYARD],
      value: InstallLocation.BACKYARD,
    },
    {
      label: YARD_OPTION_LABELS[InstallLocation.LEFTYARD],
      value: InstallLocation.LEFTYARD,
    },
    {
      label: YARD_OPTION_LABELS[InstallLocation.RIGHTYARD],
      value: InstallLocation.RIGHTYARD,
    },
  ],
  CURB_APPEAL: [
    {
      label: YARD_OPTION_LABELS[InstallLocation.FRONTYARD],
      value: InstallLocation.FRONTYARD,
    },
    {
      label: YARD_OPTION_LABELS[InstallLocation.LEFTYARD],
      value: InstallLocation.LEFTYARD,
    },
    {
      label: YARD_OPTION_LABELS[InstallLocation.RIGHTYARD],
      value: InstallLocation.RIGHTYARD,
    },
  ],
  WHOLE_HOME: [
    {
      label: YARD_OPTION_LABELS[InstallLocation.FRONTYARD],
      value: InstallLocation.FRONTYARD,
    },
    {
      label: YARD_OPTION_LABELS[InstallLocation.BACKYARD],
      value: InstallLocation.BACKYARD,
    },
    {
      label: YARD_OPTION_LABELS[InstallLocation.LEFTYARD],
      value: InstallLocation.LEFTYARD,
    },
    {
      label: YARD_OPTION_LABELS[InstallLocation.RIGHTYARD],
      value: InstallLocation.RIGHTYARD,
    },
  ],
  UBER_PREMIUM: [
    {
      label: YARD_OPTION_LABELS[InstallLocation.FRONTYARD],
      value: InstallLocation.FRONTYARD,
    },
    {
      label: YARD_OPTION_LABELS[InstallLocation.BACKYARD],
      value: InstallLocation.BACKYARD,
    },
    {
      label: YARD_OPTION_LABELS[InstallLocation.LEFTYARD],
      value: InstallLocation.LEFTYARD,
    },
    {
      label: YARD_OPTION_LABELS[InstallLocation.RIGHTYARD],
      value: InstallLocation.RIGHTYARD,
    },
  ],
};

// Helper to get yard options for a given packageType
export function getYardOptionsForPackageType(packageType: string) {
  return PACKAGE_TYPE_YARD_OPTIONS[packageType] || [];
}
